import React, { useState, useEffect, useRef } from 'react';
import { humanize } from '../../../utils/adminFormat';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  TextField,
  InputAdornment,
  Chip,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  MenuItem,
  Grid,
  Autocomplete,
  Avatar,
  Checkbox,
  FormControlLabel,
  CircularProgress,
  Divider,
  LinearProgress,
  Alert,
} from '@mui/material';
import {
  Search as SearchIcon,
  Visibility as ViewIcon,
  History as HistoryIcon,
  ClearAll as ClearAllIcon,
  Storage as StorageIcon,
  Download as DownloadIcon,
  DeleteOutline as DeleteIcon,
  DeleteSweep as DeleteSweepIcon,
  RestoreFromTrash as RestoreIcon,
} from '@mui/icons-material';
import api from '../../../api';
import { toast } from 'react-toastify';
import ResponsiveTable from '../../../components/ResponsiveTable';
import HtmlWithLatex from '../../QuestionBank/components/HtmlWithLatex';

const HTML_FIELDS = new Set(['question_text', 'explanation', 'correct_answer', 'correct_answer_text', 'section_instructions']);

const PLATFORM_COLORS = { web: 'info', mobile: 'secondary' };

const renderChangeValue = (key, value) => {
  if (value === undefined || value === null || value === '') {
    return <Typography component="span" variant="caption" color="text.disabled">—</Typography>;
  }

  if (HTML_FIELDS.has(key) && typeof value === 'string') {
    return <Box sx={{ '& p': { m: 0 } }}><HtmlWithLatex html={value} /></Box>;
  }

  if (key === 'options' && Array.isArray(value)) {
    return (
      <Box component="ul" sx={{ m: 0, pl: 2 }}>
        {value.map((o, i) => (
          <li key={i}>
            <Typography component="span" variant="body2">
              {o?.image_url ? '[image]' : (o?.text ?? '')}
            </Typography>
            {o?.is_correct && <Chip label="correct" size="small" color="success" variant="outlined" sx={{ ml: 0.5, height: 18, fontSize: '0.6rem' }} />}
          </li>
        ))}
      </Box>
    );
  }

  if (typeof value === 'object') {
    return (
      <Box component="pre" sx={{ m: 0, fontSize: 11, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
        {JSON.stringify(value, null, 1)}
      </Box>
    );
  }

  return <Typography component="span" variant="body2">{String(value)}</Typography>;
};

const ROLE_COLORS = {
  Tester: 'secondary',
  Tutor: 'info',
  Student: 'success',
  Parent: 'warning',
  Admin: 'primary',
  'Super Admin': 'error',
};

const EVENT_COLORS = {
  created: 'success',
  updated: 'info',
  deleted: 'error',
  logged_in: 'success',
  logged_out: 'default',
  login_failed: 'error',
};

const formatDateTime = (value) => {
  if (!value) return '-';
  try {
    return new Date(value).toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch {
    return value;
  }
};

function UserAvatar({ name }) {
  const initials = name
    ? name.split(' ').map((n) => n[0]).slice(0, 2).join('').toUpperCase()
    : '?';
  return (
    <Avatar sx={{ width: 28, height: 28, fontSize: '0.7rem', bgcolor: 'primary.light', color: 'primary.contrastText', flexShrink: 0 }}>
      {initials}
    </Avatar>
  );
}

const formatBytes = (bytes) => {
  const n = Number(bytes) || 0;
  if (n <= 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.min(Math.floor(Math.log(n) / Math.log(1024)), units.length - 1);
  return `${(n / 1024 ** i).toFixed(i === 0 ? 0 : 2)} ${units[i]}`;
};

const StatTile = ({ label, value, hint, color }) => (
  <Paper variant="outlined" sx={{ p: 1.5, height: '100%' }}>
    <Typography variant="caption" color="text.secondary" display="block">{label}</Typography>
    <Typography variant="h6" fontWeight={600} color={color}>{value}</Typography>
    {hint && <Typography variant="caption" color="text.secondary">{hint}</Typography>}
  </Paper>
);

const EMPTY_FILTERS = {
  searchTerm: '',
  role: '',
  logName: '',
  event: '',
  platform: '',
  dateFrom: '',
  dateTo: '',
  userId: '',
};

const AuditTrailList = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(15);
  const [totalRecords, setTotalRecords] = useState(0);

  const [searchTerm, setSearchTerm] = useState('');
  const [role, setRole] = useState('');
  const [logName, setLogName] = useState('');
  const [event, setEvent] = useState('');
  const [platform, setPlatform] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  // User autocomplete
  const [userId, setUserId] = useState('');
  const [userInputValue, setUserInputValue] = useState('');
  const [userOptions, setUserOptions] = useState([]);
  const [userLoading, setUserLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const userDebounceRef = useRef(null);

  const [filterOptions, setFilterOptions] = useState({ roles: [], log_names: [], events: [], platforms: [] });
  const [detail, setDetail] = useState({ open: false, row: null });

  const [storageOpen, setStorageOpen] = useState(false);
  const [retention, setRetention] = useState(null);
  const [retentionLoading, setRetentionLoading] = useState(false);
  const [purging, setPurging] = useState(false);
  const [previewOnly, setPreviewOnly] = useState(false);
  const [confirmPurge, setConfirmPurge] = useState(false);
  const [restoring, setRestoring] = useState(null);
  const [confirmRestore, setConfirmRestore] = useState(null);

  const hasActiveFilters = !!(searchTerm || role || logName || event || platform || dateFrom || dateTo || userId);

  useEffect(() => {
    fetchFilters();
  }, []);

  useEffect(() => {
    fetchActivities();
  }, [page, rowsPerPage, searchTerm, role, logName, event, platform, dateFrom, dateTo, userId]);

  // Debounced user search
  useEffect(() => {
    if (userDebounceRef.current) clearTimeout(userDebounceRef.current);
    if (!userInputValue) {
      setUserOptions([]);
      return;
    }
    userDebounceRef.current = setTimeout(async () => {
      setUserLoading(true);
      try {
        const res = await api.get('admin/audit-trail/suggest', { params: { q: userInputValue } });
        if (res.data?.success) setUserOptions(res.data.data ?? []);
      } catch {
        // non-blocking
      } finally {
        setUserLoading(false);
      }
    }, 300);
    return () => clearTimeout(userDebounceRef.current);
  }, [userInputValue]);

  const fetchFilters = async () => {
    try {
      const res = await api.get('admin/audit-trail/filters');
      if (res.data.success) setFilterOptions(res.data.data);
    } catch {
      // non-blocking
    }
  };

  const fetchActivities = async () => {
    setLoading(true);
    try {
      const res = await api.get('admin/audit-trail', {
        params: {
          page: page + 1,
          per_page: rowsPerPage,
          search: searchTerm,
          role,
          log_name: logName,
          event,
          platform,
          date_from: dateFrom,
          date_to: dateTo,
          user_id: userId,
        },
      });
      if (res.data.success) {
        const data = res.data.data;
        setRows(data.data || []);
        setTotalRecords(data.total || 0);
      }
    } catch (error) {
      console.error('Error fetching audit trail:', error);
      toast.error('Failed to fetch audit trail');
    } finally {
      setLoading(false);
    }
  };

  const fetchRetention = async () => {
    setRetentionLoading(true);
    try {
      const res = await api.get('admin/audit-trail/retention');
      if (res.data.success) setRetention(res.data.data);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to load storage details');
    } finally {
      setRetentionLoading(false);
    }
  };

  const openStorage = () => {
    setStorageOpen(true);
    setPreviewOnly(false);
    fetchRetention();
  };

  const runPurge = async () => {
    setConfirmPurge(false);
    setPurging(true);
    try {
      const res = await api.post('admin/audit-trail/purge', { dry_run: previewOnly });
      const data = res.data.data;
      if (data.dry_run) {
        toast.info(`Preview: ${data.eligible.toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })} entries would be archived and removed.`);
      } else {
        toast.success(
          `Archived ${data.deleted.toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })} entries to ${data.file} (${formatBytes(data.bytes)}).`
        );
        if (data.remaining > 0) {
          toast.info(`${data.remaining.toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })} more are eligible — run again, or use "php artisan audit:prune" on the server.`);
        }
      }
      await fetchRetention();
      if (!data.dry_run) {
        setPage(0);
        fetchActivities();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Purge failed');
    } finally {
      setPurging(false);
    }
  };

  const downloadArchive = async (name) => {
    try {
      const res = await api.get(`admin/audit-trail/archives/${name}`, { responseType: 'blob' });
      if (res.data.type === 'application/json') {
        const text = await res.data.text();
        throw new Error(JSON.parse(text)?.message || 'Download failed');
      }
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', name);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(error.message || 'Failed to download archive');
    }
  };

  const restoreArchive = async (name) => {
    setRestoring(name);
    try {
      const res = await api.post(`admin/audit-trail/archives/${name}/restore`);
      const data = res.data.data;
      toast.success(`Restored ${Number(data.restored).toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })} entries from ${data.file}.`);
      await fetchRetention();
      setPage(0);
      fetchActivities();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Restore failed');
    } finally {
      setRestoring(null);
    }
  };

  const deleteArchive = async (name) => {
    try {
      await api.delete(`admin/audit-trail/archives/${name}`);
      toast.success('Archive deleted');
      fetchRetention();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to delete archive');
    }
  };

  const resetToFirstPage = (setter) => (e) => {
    setPage(0);
    setter(e.target.value);
  };

  const clearAllFilters = () => {
    setPage(0);
    setSearchTerm('');
    setRole('');
    setLogName('');
    setEvent('');
    setPlatform('');
    setDateFrom('');
    setDateTo('');
    setUserId('');
    setSelectedUser(null);
    setUserInputValue('');
    setUserOptions([]);
  };

  const renderChanges = (changes) => {
    if (!changes) return null;
    const oldVals = changes.old || {};
    const newVals = changes.new || {};
    const keys = Array.from(new Set([...Object.keys(oldVals), ...Object.keys(newVals)]));
    if (keys.length === 0) {
      return <Typography variant="body2" color="text.secondary">No field-level changes recorded.</Typography>;
    }
    return (
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Field</TableCell>
            <TableCell>Before</TableCell>
            <TableCell>After</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {keys.map((key) => (
            <TableRow key={key}>
              <TableCell sx={{ verticalAlign: 'top' }}><strong>{key}</strong></TableCell>
              <TableCell sx={{ wordBreak: 'break-word', borderLeft: '3px solid', borderColor: 'error.light', verticalAlign: 'top' }}>
                {renderChangeValue(key, oldVals[key])}
              </TableCell>
              <TableCell sx={{ wordBreak: 'break-word', borderLeft: '3px solid', borderColor: 'success.light', verticalAlign: 'top' }}>
                {renderChangeValue(key, newVals[key])}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    );
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <HistoryIcon color="primary" />
        <Typography variant="h5" fontWeight={600}>Audit Trail</Typography>
        <Box sx={{ flexGrow: 1 }} />
        {filterOptions.can_purge && (
          <Button
            size="small"
            variant="outlined"
            startIcon={<StorageIcon />}
            onClick={openStorage}
            sx={{ textTransform: 'none' }}
          >
            Log storage
          </Button>
        )}
      </Box>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth size="small" label="Search action / resource"
              value={searchTerm}
              onChange={resetToFirstPage(setSearchTerm)}
              InputProps={{ startAdornment: (<InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>) }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <Autocomplete
              options={userOptions}
              getOptionLabel={(o) => (typeof o === 'string' ? o : `${o.name} (${o.email})`)}
              isOptionEqualToValue={(o, v) => o.id === v.id}
              value={selectedUser}
              inputValue={userInputValue}
              loading={userLoading}
              onInputChange={(_, val) => setUserInputValue(val)}
              onChange={(_, selected) => {
                setPage(0);
                setSelectedUser(selected);
                setUserId(selected ? String(selected.id) : '');
              }}
              filterOptions={(x) => x}
              clearOnEscape
              size="small"
              renderOption={(props, option) => (
                <li {...props} key={option.id}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <UserAvatar name={option.name} />
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{option.name}</Typography>
                      <Typography variant="caption" color="text.secondary">{option.email}</Typography>
                    </Box>
                  </Box>
                </li>
              )}
              renderInput={(params) => (
                <TextField {...params} label="User" placeholder="Search by name or email" />
              )}
            />
          </Grid>
          <Grid item xs={6} md={2}>
            <TextField select fullWidth size="small" label="Role" value={role} onChange={resetToFirstPage(setRole)}>
              <MenuItem value="">All roles</MenuItem>
              {filterOptions.roles.map((r) => (<MenuItem key={r} value={r}>{r}</MenuItem>))}
            </TextField>
          </Grid>
          <Grid item xs={6} md={2}>
            <TextField select fullWidth size="small" label="Module" value={logName} onChange={resetToFirstPage(setLogName)}>
              <MenuItem value="">All modules</MenuItem>
              {filterOptions.log_names.map((l) => (<MenuItem key={l} value={l}>{humanize(l)}</MenuItem>))}
            </TextField>
          </Grid>
          <Grid item xs={6} md={2}>
            <TextField select fullWidth size="small" label="Action" value={event} onChange={resetToFirstPage(setEvent)}>
              <MenuItem value="">All actions</MenuItem>
              {filterOptions.events.map((ev) => (<MenuItem key={ev} value={ev}>{ev}</MenuItem>))}
            </TextField>
          </Grid>
          <Grid item xs={6} md={2}>
            <TextField select fullWidth size="small" label="Platform" value={platform} onChange={resetToFirstPage(setPlatform)}>
              <MenuItem value="">All platforms</MenuItem>
              {(filterOptions.platforms || []).map((p) => (<MenuItem key={p} value={p}>{p}</MenuItem>))}
            </TextField>
          </Grid>
          <Grid item xs={6} md={1.5}>
            <TextField fullWidth size="small" type="date" label="From" InputLabelProps={{ shrink: true }}
              value={dateFrom} onChange={resetToFirstPage(setDateFrom)} />
          </Grid>
          <Grid item xs={6} md={1.5}>
            <TextField fullWidth size="small" type="date" label="To" InputLabelProps={{ shrink: true }}
              value={dateTo} onChange={resetToFirstPage(setDateTo)} />
          </Grid>
          {hasActiveFilters && (
            <Grid item xs={12} md="auto" sx={{ display: 'flex', alignItems: 'center' }}>
              <Button
                size="small"
                variant="outlined"
                color="inherit"
                startIcon={<ClearAllIcon />}
                onClick={clearAllFilters}
                sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
              >
                Clear filters
              </Button>
            </Grid>
          )}
        </Grid>
      </Paper>

      <Paper>
        <ResponsiveTable>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>When</TableCell>
                <TableCell>User</TableCell>
                <TableCell>Role</TableCell>
                <TableCell>Action</TableCell>
                <TableCell>Resource</TableCell>
                <TableCell>Platform</TableCell>
                <TableCell>IP</TableCell>
                <TableCell align="center">Details</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 && !loading && (
                <TableRow><TableCell colSpan={8} align="center" sx={{ py: 4 }}>No activity found.</TableCell></TableRow>
              )}
              {rows.map((row) => (
                <TableRow key={row.id} hover>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    <Typography variant="body2">{row.created_at ? new Date(row.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '-'}</Typography>
                    <Typography variant="caption" color="text.secondary">{row.created_at ? new Date(row.created_at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : ''}</Typography>
                  </TableCell>
                  <TableCell>
                    {row.causer ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <UserAvatar name={row.causer.name} />
                        <Box>
                          <Typography variant="body2" fontWeight={500}>{row.causer.name || row.causer.username}</Typography>
                          <Typography variant="caption" color="text.secondary">{row.causer.email}</Typography>
                        </Box>
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        {row.attempted_email ? `${row.attempted_email} (unauthenticated)` : 'System'}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {row.causer_role ? (
                      <Box>
                        <Chip size="small" label={row.causer_role} color={ROLE_COLORS[row.causer_role] || 'default'} />
                        {row.causer_role_group && row.causer_role_group !== row.causer_role?.toLowerCase().replace(' ', '_') && (
                          <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.25 }}>
                            {row.causer_role_group}
                          </Typography>
                        )}
                      </Box>
                    ) : '-'}
                  </TableCell>
                  <TableCell>
                    <Chip size="small" variant="outlined" label={humanize(row.event || row.description)}
                      color={EVENT_COLORS[row.event] || 'default'} />
                    {row.log_name && (
                      <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.25 }}>
                        {humanize(row.log_name)}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {row.subject_type ? (
                      <Typography variant="body2">
                        {row.subject_label || row.subject_type}
                        <Typography component="span" variant="caption" color="text.secondary"> #{row.subject_id}</Typography>
                      </Typography>
                    ) : (
                      <Typography variant="caption" color="text.secondary">{row.log_name}</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {row.platform
                      ? <Chip size="small" variant="outlined" label={row.platform} color={PLATFORM_COLORS[row.platform] || 'default'} />
                      : <Typography variant="caption" color="text.secondary">-</Typography>}
                  </TableCell>
                  <TableCell><Typography variant="caption">{row.ip_address || '-'}</Typography></TableCell>
                  <TableCell align="center">
                    <Tooltip title="View details">
                      <IconButton size="small" color="primary" onClick={() => setDetail({ open: true, row })}>
                        <ViewIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </ResponsiveTable>
        <TablePagination
          component="div"
          count={totalRecords}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[15, 25, 50, 100]}
        />
      </Paper>

      <Dialog open={detail.open} onClose={() => setDetail({ open: false, row: null })} maxWidth="md" fullWidth>
        <DialogTitle>Activity Details</DialogTitle>
        <DialogContent dividers>
          {detail.row && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Typography variant="body2"><strong>When:</strong> {formatDateTime(detail.row.created_at)}</Typography>
              <Typography variant="body2">
                <strong>User:</strong> {detail.row.causer ? `${detail.row.causer.name} (${detail.row.causer.email})` : (detail.row.attempted_email || 'System')}
                {detail.row.causer_role ? ` — ${detail.row.causer_role}` : ''}
                {detail.row.causer_role_group ? ` [${detail.row.causer_role_group}]` : ''}
              </Typography>
              <Typography variant="body2"><strong>Action:</strong> {detail.row.event || detail.row.description} ({detail.row.log_name})</Typography>
              <Typography variant="body2"><strong>Resource:</strong> {detail.row.subject_type ? `${detail.row.subject_label || detail.row.subject_type} #${detail.row.subject_id}` : '—'}</Typography>
              {(detail.row.method || detail.row.path) && (
                <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                  <strong>Endpoint:</strong> {detail.row.method} /{detail.row.path}
                  {detail.row.status ? ` → ${detail.row.status}` : ''}
                </Typography>
              )}
              {detail.row.route_params && Object.keys(detail.row.route_params).length > 0 && (
                <Typography variant="body2"><strong>Params:</strong> {JSON.stringify(detail.row.route_params)}</Typography>
              )}
              {detail.row.platform && (
                <Typography variant="body2"><strong>Platform:</strong> {detail.row.platform}</Typography>
              )}
              <Typography variant="body2"><strong>IP:</strong> {detail.row.ip_address || '-'}</Typography>
              <Typography variant="body2" sx={{ wordBreak: 'break-word' }}><strong>User agent:</strong> {detail.row.user_agent || '-'}</Typography>
              {detail.row.payload && Object.keys(detail.row.payload).length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>Request payload</Typography>
                  <Box component="pre" sx={{ m: 0, p: 1, bgcolor: 'grey.100', borderRadius: 1, fontSize: 12, overflowX: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                    {JSON.stringify(detail.row.payload, null, 2)}
                  </Box>
                </Box>
              )}
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" gutterBottom>Changes</Typography>
                {renderChanges(detail.row.changes)}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetail({ open: false, row: null })}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={storageOpen} onClose={() => !purging && setStorageOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <StorageIcon color="primary" /> Log storage &amp; retention
        </DialogTitle>
        <DialogContent dividers>
          {(retentionLoading || purging) && <LinearProgress sx={{ mb: 2 }} />}
          {!retention && retentionLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
          )}
          {retention && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Grid container spacing={1.5}>
                <Grid item xs={6} md={3}>
                  <StatTile label="Size on disk" value={formatBytes(retention.stats.table_bytes)}
                    hint={`~${formatBytes(retention.stats.avg_row_bytes)} per entry`} />
                </Grid>
                <Grid item xs={6} md={3}>
                  <StatTile label="Total entries" value={Number(retention.stats.total_rows).toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                    hint={retention.stats.oldest_at ? `since ${new Date(retention.stats.oldest_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}` : ''} />
                </Grid>
                <Grid item xs={6} md={3}>
                  <StatTile label="Eligible to purge" value={Number(retention.stats.prunable_rows).toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                    color="error.main" hint={`before ${new Date(retention.stats.routine_cutoff).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}`} />
                </Grid>
                <Grid item xs={6} md={3}>
                  <StatTile label="Reclaims about" value={formatBytes(retention.stats.estimated_reclaim_bytes)}
                    color="success.main" hint="estimate" />
                </Grid>
              </Grid>

              <Alert severity="info" sx={{ py: 0.5 }}>
                Routine activity is kept {retention.stats.default_days} days. Logins and deletions are kept{' '}
                {retention.stats.extended_days} days. Entries are written to a downloadable archive file before
                they are removed from the database.
              </Alert>

              {retention.stats.by_month?.length > 0 && (
                <Box>
                  <Typography variant="subtitle2" gutterBottom>Entries by month</Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    {retention.stats.by_month.map((m) => {
                      const max = Math.max(...retention.stats.by_month.map((x) => x.rows));
                      return (
                        <Box key={m.month} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="caption" sx={{ width: 64, flexShrink: 0 }}>{m.month}</Typography>
                          <Box sx={{ flexGrow: 1, bgcolor: 'action.hover', borderRadius: 0.5, height: 14 }}>
                            <Box sx={{ width: `${max ? (m.rows / max) * 100 : 0}%`, bgcolor: 'primary.main', height: '100%', borderRadius: 0.5 }} />
                          </Box>
                          <Typography variant="caption" sx={{ width: 70, textAlign: 'right', flexShrink: 0 }}>
                            {m.rows.toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                          </Typography>
                        </Box>
                      );
                    })}
                  </Box>
                </Box>
              )}

              <Divider />

              <Box>
                <Typography variant="subtitle2" gutterBottom>Archives</Typography>
                {retention.archives.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">No archives yet.</Typography>
                ) : (
                  <Table size="small">
                    <TableBody>
                      {retention.archives.map((a) => (
                        <TableRow key={a.name}>
                          <TableCell sx={{ wordBreak: 'break-all' }}>
                            <Typography variant="body2">{a.name}</Typography>
                            <Typography variant="caption" color="text.secondary">{formatDateTime(a.created_at)}</Typography>
                          </TableCell>
                          <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                            <Typography variant="caption">{formatBytes(a.bytes)}</Typography>
                          </TableCell>
                          <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                            <Tooltip title="Download">
                              <IconButton size="small" onClick={() => downloadArchive(a.name)}>
                                <DownloadIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Restore into the database">
                              <IconButton
                                size="small"
                                color="primary"
                                disabled={!!restoring}
                                onClick={() => setConfirmRestore(a)}
                              >
                                {restoring === a.name
                                  ? <CircularProgress size={18} />
                                  : <RestoreIcon fontSize="small" />}
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete archive">
                              <IconButton size="small" color="error" disabled={!!restoring} onClick={() => deleteArchive(a.name)}>
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-between', px: 3 }}>
          <FormControlLabel
            control={<Checkbox size="small" checked={previewOnly} onChange={(e) => setPreviewOnly(e.target.checked)} />}
            label={<Typography variant="body2">Preview only</Typography>}
          />
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button onClick={() => setStorageOpen(false)} disabled={purging}>Close</Button>
            <Button
              variant="contained"
              color="error"
              startIcon={<DeleteSweepIcon />}
              disabled={purging || retentionLoading || !retention || retention.stats.prunable_rows === 0}
              onClick={() => (previewOnly ? runPurge() : setConfirmPurge(true))}
            >
              {purging ? 'Working…' : previewOnly ? 'Preview' : 'Archive & purge now'}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      <Dialog open={confirmPurge} onClose={() => setConfirmPurge(false)}>
        <DialogTitle>Archive &amp; purge audit log</DialogTitle>
        <DialogContent>
          <Typography>
            {Number(retention?.stats?.prunable_rows || 0).toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })} entries older than{' '}
            {retention?.stats?.default_days} days will be written to a downloadable archive file and then
            deleted from the database. Logins and deletions are not affected.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmPurge(false)}>Cancel</Button>
          <Button onClick={runPurge} color="error" variant="contained">Archive &amp; purge</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!confirmRestore} onClose={() => setConfirmRestore(null)}>
        <DialogTitle>Restore archive</DialogTitle>
        <DialogContent>
          <Typography>
            Entries from <strong>{confirmRestore?.name}</strong> will be written back into the database.
            Entries that are still present are left untouched, so restoring twice is safe. This will
            increase the table size again.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmRestore(null)}>Cancel</Button>
          <Button
            onClick={() => { const n = confirmRestore.name; setConfirmRestore(null); restoreArchive(n); }}
            color="primary"
            variant="contained"
          >
            Restore
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AuditTrailList;
